// src/services/productService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class ProductService {
  // Get all products
  async getAllProducts(params = {}) {
    try {
      console.log('🛍️ Fetching all products with params:', params)
      const response = await ApiService.get(AppURLs.PRODUCTS.GET_ALL, params)
      console.log('✅ Products fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching products:', error)
      throw error
    }
  }

  // Get product by ID
  async getProductById(id) {
    try {
      console.log('🛍️ Fetching product by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Product fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching product:', error)
      throw error
    }
  }

  // Create new product
  async createProduct(productData) {
    try {
      console.log('🛍️ Creating new product with data:', productData)
      const response = await ApiService.post(AppURLs.PRODUCTS.CREATE, productData)
      console.log('✅ Product created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating product:', error)
      throw error
    }
  }

  // Update product using PATCH
  async updateProduct(id, productData) {
    try {
      console.log('🛍️ Updating product:', { id, productData })
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)

      // Use PATCH for updates
      const response = await ApiService.patch(endpoint, productData)
      console.log('✅ Product updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating product:', error)
      throw error
    }
  }

  // Delete product
  async deleteProduct(id) {
    try {
      console.log('🛍️ Deleting product:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Product deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting product:', error)
      throw error
    }
  }

  // Check if product is assigned to branches
  async checkProductAssignment(id) {
    try {
      console.log('🛍️ Checking product assignment:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.CHECK_ASSIGNMENT, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Product assignment check completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error checking product assignment:', error)
      throw error
    }
  }

  // Toggle product status
  async toggleProductStatus(id, isActive) {
    try {
      console.log('🛍️ Toggling product status:', { id, isActive })
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.UPDATE, { id })
      const response = await ApiService.patch(endpoint, { isActive })
      console.log('✅ Product status toggled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling product status:', error)
      throw error
    }
  }

  // Bulk operations
  async bulkDeleteProducts(productIds) {
    try {
      console.log('🛍️ Bulk deleting products:', productIds)
      const response = await ApiService.post(AppURLs.PRODUCTS.BULK_DELETE, {
        ids: productIds
      })
      console.log('✅ Products bulk deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting products:', error)
      throw error
    }
  }

  // Bulk status update
  async bulkUpdateStatus(productIds, isActive) {
    try {
      console.log('🛍️ Bulk updating product status:', { productIds, isActive })
      const response = await ApiService.patch(AppURLs.PRODUCTS.BULK_STATUS, {
        ids: productIds,
        isActive
      })
      console.log('✅ Products bulk status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating product status:', error)
      throw error
    }
  }

  // Get product statistics
  async getProductStats() {
    try {
      console.log('🛍️ Fetching product statistics')
      const response = await ApiService.get(AppURLs.PRODUCTS.STATS)
      console.log('✅ Product statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching product statistics:', error)
      throw error
    }
  }

  // Validate product name (check for duplicates)
  async validateProductName(name, excludeId = null) {
    try {
      console.log('🛍️ Validating product name:', { name, excludeId })
      const params = {
        name,
        ...(excludeId && { excludeId })
      }
      const response = await ApiService.get(AppURLs.PRODUCTS.VALIDATE_NAME, params)
      console.log('✅ Product name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating product name:', error)
      throw error
    }
  }

  // Validate product SKU (check for duplicates)
  async validateProductSKU(sku, excludeId = null) {
    try {
      console.log('🛍️ Validating product SKU:', { sku, excludeId })
      const params = {
        sku,
        ...(excludeId && { excludeId })
      }
      const response = await ApiService.get(AppURLs.PRODUCTS.VALIDATE_SKU, params)
      console.log('✅ Product SKU validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating product SKU:', error)
      throw error
    }
  }

  // Get products by category
  async getProductsByCategory(categoryId) {
    try {
      console.log('🛍️ Fetching products by category:', categoryId)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.BY_CATEGORY, { categoryId })
      const response = await ApiService.get(endpoint)
      console.log('✅ Products by category fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching products by category:', error)
      throw error
    }
  }

  // Get products by type
  async getProductsByType(type) {
    try {
      console.log('🛍️ Fetching products by type:', type)
      const endpoint = ApiService.replaceUrlParams(AppURLs.PRODUCTS.BY_TYPE, { type })
      const response = await ApiService.get(endpoint)
      console.log('✅ Products by type fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching products by type:', error)
      throw error
    }
  }

  // Search products
  async searchProducts(searchTerm, filters = {}) {
    try {
      console.log('🛍️ Searching products:', { searchTerm, filters })
      const params = {
        search: searchTerm,
        ...filters
      }
      const response = await ApiService.get(AppURLs.PRODUCTS.SEARCH, params)
      console.log('✅ Products search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching products:', error)
      throw error
    }
  }

  // Get products with pagination
  async getProductsPaginated(page = 1, limit = 10, filters = {}) {
    try {
      console.log('🛍️ Fetching paginated products:', { page, limit, filters })
      const params = {
        page,
        limit,
        ...filters
      }
      const response = await ApiService.get(AppURLs.PRODUCTS.GET_ALL, params)
      console.log('✅ Paginated products fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching paginated products:', error)
      throw error
    }
  }

  // Get products suitable for recipes (only recipe_based products)
  async getProductsForRecipes(params = {}) {
    try {
      console.log('🍳 Fetching products for recipes with params:', params)
      // Filter for recipe_based products or all products depending on your needs
      const response = await this.getAllProducts({
        ...params,
        type: 'recipe_based' // Only get recipe-based products
      })
      console.log('✅ Recipe products fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching recipe products:', error)
      throw error
    }
  }

  // 🔍 Helper Methods for Frontend

  // Format product data for display
  formatProductForDisplay(product) {
    if (!product) return null

    return {
      ...product,
      formattedCost: this.formatCurrency(product.cost),
      formattedOutletPrice: this.formatCurrency(product.outletPrice),
      formattedPickMePrice: this.formatCurrency(product.pickMePrice),
      formattedUberPrice: this.formatCurrency(product.uberPrice),
      categoryName: product.categoryDetails?.name || 'Category not found',
      unitName: product.unitDetails?.name || 'Unit not found',
      unitAbbreviation: product.unitDetails?.abbreviation || ''
    }
  }

  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  // Calculate profit margin
  calculateProfitMargin(cost, sellingPrice) {
    if (!cost || !sellingPrice || cost <= 0) return 0
    return ((sellingPrice - cost) / cost) * 100
  }

  // Get product type options
  getProductTypes() {
    return [
      { value: 'product', label: 'Product', icon: '📦' },
      { value: 'recipe_based', label: 'Recipe Based', icon: '🍳' }
    ]
  }

  // 🛡️ Validation Methods
  validateProductData(productData) {
    const { name, sku, category, type, unit, threshold, cost, outletPrice, pickMePrice, uberPrice } = productData

    // Check required fields
    if (!name || !name.trim()) {
      throw new Error('Product name is required')
    }

    if (!sku || !sku.trim()) {
      throw new Error('SKU is required')
    }

    if (!category) {
      throw new Error('Category is required')
    }

    if (!type) {
      throw new Error('Product type is required')
    }

    if (!unit) {
      throw new Error('Unit is required')
    }

    // Validate numeric fields
    if (isNaN(threshold) || threshold < 0) {
      throw new Error('Threshold must be a valid number >= 0')
    }

    if (isNaN(cost) || cost < 0) {
      throw new Error('Cost must be a valid number >= 0')
    }

    if (isNaN(outletPrice) || outletPrice < 0) {
      throw new Error('Outlet price must be a valid number >= 0')
    }

    if (isNaN(pickMePrice) || pickMePrice < 0) {
      throw new Error('PickMe price must be a valid number >= 0')
    }

    if (isNaN(uberPrice) || uberPrice < 0) {
      throw new Error('Uber price must be a valid number >= 0')
    }

    return true
  }

  // 🔍 Client-side filtering methods
  searchProducts(products, searchTerm) {
    if (!searchTerm || !Array.isArray(products)) return products

    const lowercaseSearch = searchTerm.toLowerCase()
    return products.filter(product => {
      const name = product.name?.toLowerCase() || ''
      const sku = product.sku?.toLowerCase() || ''
      const categoryName = product.categoryDetails?.name?.toLowerCase() || ''

      return name.includes(lowercaseSearch) ||
        sku.includes(lowercaseSearch) ||
        categoryName.includes(lowercaseSearch)
    })
  }

  filterByCategory(products, categoryId) {
    if (!categoryId || !Array.isArray(products)) return products
    return products.filter(product => product.category === categoryId)
  }

  filterByType(products, type) {
    if (!type || !Array.isArray(products)) return products
    return products.filter(product => product.type === type)
  }

  filterByStatus(products, isActive) {
    if (typeof isActive !== 'boolean' || !Array.isArray(products)) return products
    return products.filter(product => product.isActive === isActive)
  }
}

export default new ProductService()